<template>
  <tab-layout :full-width="true">
    <div slot="tabContent" class="bg-white rounded mb-4 p-3">
      <page-heading>Reports</page-heading>

      <reports-filter-bar
        :years="filterState.years"
        :year="filterState.year"
        :drafts="filterState.drafts"
        :draft-id="filterState.draftId"
        :drafts-loading="filterState.draftsLoading"
        active="outgoing"
        @year-change="onYearChange"
        @draft-change="onDraftChange"
      />

      <template v-if="filterState.draftId != null">
        <div class="mb-3">
          <app-button variant="primary" :block="false" :loading="generating" :disabled="unfinalizedEvent || queueNotEmpty" @click="onGenerate">Generate prize money reports</app-button>
          <small v-if="unfinalizedEvent" class="d-block text-muted mt-1">Prize money report for a draft can not be generated until all its events are finalised.</small>
          <small v-else-if="queueNotEmpty" class="d-block text-muted mt-1">Invoices are in the process of generation, please wait.</small>
        </div>

        <div class="d-flex flex-wrap align-items-end mb-3">
          <div class="d-flex col-12 col-md-auto px-0 mb-3 mb-md-2 mr-3">
            <div class="filter">
              <div class="form-check pl-0" v-for="s in statusFilters" :key="s.id">
                <input
                  class="form-check-input"
                  type="radio"
                  name="statusFilter"
                  :id="'statusFilter_' + s.id"
                  :value="s.id"
                  :checked="activeStatusFilter === s.id"
                  @change="onStatusFilter(s.id)"
                />
                <label class="form-check-label" :for="'statusFilter_' + s.id">{{ s.label }}</label>
              </div>
            </div>
          </div>

          <div class="form-group ml-auto mb-2">
            <div class="input-append d-flex">
              <input v-model="searchInput" type="text" class="form-control mb-0" style="min-width:180px" placeholder="Search..." @keyup.enter="setSearch(searchInput)" />
              <div class="filter">
                <div class="form-check pl-2 mb-0">
                  <input class="form-check-input" type="radio" id="outSearchBtn" style="display:none" />
                  <label class="form-check-label font-weight-normal" for="outSearchBtn" style="cursor:pointer" @click="setSearch(searchInput)">Search</label>
                </div>
                <div class="form-check pl-2 mb-0">
                  <input class="form-check-input" type="radio" id="outResetBtn" style="display:none" />
                  <label class="form-check-label font-weight-normal" for="outResetBtn" style="cursor:pointer" @click="searchInput = ''; resetSearch()">Reset</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <bulk-editor-tool
          :total-count="items.length"
          :selected-count="selectedCount"
          @toggle-all="onToggleAll"
          class="mb-3"
        >
          <div class="bulk-action-group mr-3">
            <label class="bulk-action-group__label">Payment type</label>
            <div class="d-flex align-items-center">
              <app-multiselect
                :value="bulkPaymentType"
                :options="paymentTypes"
                label="label"
                track-by="id"
                :searchable="false"
                :allow-empty="true"
                placeholder="-"
                select-label=""
                deselect-label=""
                selected-label=""
                class="bulk-action-group__select mr-2"
                @input="bulkPaymentType = $event"
              />
              <app-button
                type="button"
                variant="outline-primary"
                :block="false"
                :disabled="selectedCount === 0 || bulkPaymentType == null || bulkLoading"
                @click="onApplyPaymentType"
              >
                <span v-if="bulkLoading" class="spinner-border spinner-border-sm" role="status" />
                <span v-else>Apply</span>
              </app-button>
            </div>
          </div>
          <div class="bulk-action-group mr-3">
            <label class="bulk-action-group__label">Payment status</label>
            <div class="d-flex align-items-center">
              <app-multiselect
                :value="bulkPaymentStatus"
                :options="statusOptions"
                label="label"
                track-by="id"
                :searchable="false"
                :allow-empty="true"
                placeholder="-"
                select-label=""
                deselect-label=""
                selected-label=""
                class="bulk-action-group__select mr-2"
                @input="bulkPaymentStatus = $event"
              />
              <app-button
                type="button"
                variant="outline-primary"
                :block="false"
                :disabled="selectedCount === 0 || bulkPaymentStatus == null || bulkLoading"
                @click="onApplyPaymentStatus"
              >
                <span v-if="bulkLoading" class="spinner-border spinner-border-sm" role="status" />
                <span v-else>Apply</span>
              </app-button>
            </div>
          </div>
          <div class="bulk-action-group">
            <label class="bulk-action-group__label">Edit refund amount</label>
            <div class="d-flex align-items-center">
              <app-multiselect
                :value="bulkRefund"
                :options="refundTypes"
                label="label"
                track-by="id"
                :searchable="false"
                :allow-empty="true"
                placeholder="-"
                select-label=""
                deselect-label=""
                selected-label=""
                class="bulk-action-group__select mr-2"
                @input="bulkRefund = $event"
              />
              <app-button
                type="button"
                variant="outline-primary"
                :block="false"
                :disabled="selectedCount === 0 || bulkRefund == null || bulkLoading"
                @click="onApplyRefund"
              >
                <span v-if="bulkLoading" class="spinner-border spinner-border-sm" role="status" />
                <span v-else>Apply</span>
              </app-button>
            </div>
          </div>
        </bulk-editor-tool>

        <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
          <app-button variant="outline-primary" size="sm" class="mr-2" :block="false" :loading="exportingFormat === 'pdf'" :disabled="exportingFormat != null" @click="exportReport('pdf')">Download PDF</app-button>
          <app-button variant="outline-primary" size="sm" class="mr-2" :block="false" :loading="exportingFormat === 'csv'" :disabled="exportingFormat != null" @click="exportReport('csv')">Download CSV</app-button>
          <app-button variant="outline-primary" size="sm" :block="false" :loading="abaLoading" :disabled="abaLoading" @click="onExportAba">Download ABA</app-button>
          <input
            type="text"
            class="form-control form-control-sm ml-2 mr-2"
            placeholder="APCA"
            maxlength="6"
            style="width: 160px"
            v-model="apcaNumber"
          />
          <small class="text-muted">Please make sure that the members' and committees' bank details are filled in their profiles for the ABA file to work correctly (ACC name, BSB, ACC number).</small>
        </div>

        <form-alert :message="alertMessage" :type="alertType" />

        <div class="position-relative" style="min-height: 300px">
          <grid-table
            :columns="outgoingColumns"
            :rows="items"
            :loading="loading"
            :sort="filters.sort"
            :direction="filters.direction"
            @sort="setSort"
          >
            <template #before-row="{ row }">
              <row-alert
                v-if="rowAlert.alert.value.message && rowAlert.alert.value.rowId === row.id"
                :message="rowAlert.alert.value.message"
                :type="rowAlert.alert.value.type"
                :alert-key="rowAlert.alert.value.id"
                @dismiss="rowAlert.clear()"
              />
            </template>
            <template #cell:invoiceNumber="{ row }">
              <a
                v-if="row.pdfUrl"
                :href="row.pdfUrl"
                target="_blank"
                rel="noopener"
                class="text-primary"
              >{{ row.invoiceNumber }}</a>
              <span v-else-if="generatingPdfIds.has(row.id)" class="d-inline-flex align-items-center text-muted" style="gap:5px">
                <span class="outgoing-pdf-spinner" />{{ row.invoiceNumber }}
              </span>
              <a
                v-else-if="row.canGeneratePdf"
                href="#"
                class="text-muted"
                @click.prevent="generateInvoicePdf(row)"
              >{{ row.invoiceNumber }}</a>
              <span v-else class="text-muted">{{ row.invoiceNumber }}</span>
            </template>
            <template #cell:paymentType="{ row }">
              <select class="form-control form-control-sm w-100" :value="row.paymentType" @change="onInlinePaymentType(row, $event.target.value)">
                <option value="">—</option>
                <option
                  v-for="t in paymentTypes"
                  :key="t.id"
                  :value="t.id"
                >{{ t.label }}</option>
              </select>
            </template>
            <template #cell:amount="{ row }">
              <div v-if="editingId !== row.id" style="display: flex; width: 100%; justify-content: space-between; align-items: center">
                <span>{{ row.amount }}</span>
                <a href="#" class="icon-btn icon-btn--edit" title="Edit amount" @click.prevent="startEdit(row)" />
              </div>
              <div v-else class="input-append d-flex align-items-center">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-control flex-grow-1 mr-2 mb-0"
                  v-model="editAmount"
                  @keyup.enter="saveAmount(row)"
                  @keyup.esc="cancelEdit"
                />
                <button class="btn btn-outline-primary flex-shrink-0" @click="saveAmount(row)">Save</button>
              </div>
            </template>
            <template #cell:status="{ row }">
              <select class="form-control form-control-sm" :value="row.status" @change="onInlinePaymentStatus(row, $event.target.value)">
                <option v-for="s in statusOptions" :key="s.id" :value="s.id">{{ s.label }}</option>
              </select>
            </template>
            <template #cell:actions="{ row }">
              <app-checkbox :id="`outgoing_select_${row.id}`" :value="row.selected" @input="row.selected = $event" />
            </template>
            <template #cell:details="{ row, toggle, isOpen }">
              <a
                href="#"
                class="icon-btn"
                :class="isOpen ? 'icon-btn--events--open' : 'icon-btn--events'"
                title="Bank details"
                @click.prevent="toggle"
              />
            </template>

            <template #expand="{ row, odd }">
              <table class="table table-bordered mb-0">
                <tr :style="{ background: odd ? 'var(--white)' : 'var(--gray-100)' }">
                  <td>ACC name</td>
                  <td>BSB</td>
                  <td>ACC number</td>
                </tr>
                <tr :style="{ background: odd ? 'var(--gray-100)' : 'var(--white)' }">
                  <td>{{ row.bank && row.bank.account_name ? row.bank.account_name : '—' }}</td>
                  <td>{{ row.bank && row.bank.bsb ? row.bank.bsb : '—' }}</td>
                  <td>{{ row.bank && row.bank.account_number ? row.bank.account_number : '—' }}</td>
                </tr>
              </table>
            </template>
          </grid-table>
        </div>

        <reports-totals :totals="totals" class="mt-3" />

        <pagination :total-pages="lastPage" :page="currentPage" :on-change="setPage" class="mt-3" />

        <div class="d-flex align-items-center mt-2">
          <span class="mr-1">Show</span>
          <app-multiselect
            :value="filters.limit"
            :options="entriesOptions"
            :searchable="false"
            :allow-empty="false"
            select-label=""
            deselect-label=""
            selected-label=""
            style="width: 70px"
            class="mr-1"
            @input="setLimit"
          />
          <span>Entries</span>
        </div>
      </template>
    </div>
  </tab-layout>
</template>

<script>
import { defineComponent, ref, computed, watch, getCurrentInstance, onMounted, onBeforeUnmount } from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import AppButton from '@/components/AppButton'
import FormAlert from '@/components/FormAlert'
import RowAlert from '@/components/RowAlert'
import useRowAlert from '@/composables/useRowAlert'
import GridTable from '@/components/GridTable'
import Pagination from '@/components/Pagination'
import AppCheckbox from '@/components/AppCheckbox'
import AppMultiselect from '@/components/AppMultiselect'
import ReportsFilterBar from '@/components/Reports/ReportsFilterBar'
import ReportsTotals from '@/components/Reports/ReportsTotals'
import BulkEditorTool from '@/components/BulkEditorTool'
import useReportsOutgoing from '@/composables/reports/useReportsOutgoing'
import useReportsFilter from '@/composables/reports/useReportsFilter'
import reportsApi from '@/services/api/reports'

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'paid', label: 'Paid' },
  { id: 'unpaid', label: 'Unpaid' }
]

const ENTRIES_OPTIONS = [20, 50, 100, 200]

const OUTGOING_COLUMNS = [
  { key: 'date', label: 'Date sent', sortable: true, primary: true, width: '1fr' },
  { key: 'invoiceNumber', label: 'Invoice number', sortable: true, mobile: 'label', width: '1fr' },
  { key: 'competitorName', label: 'Competitor name', sortable: true, mobile: 'label', width: '1.5fr' },
  { key: 'paymentType', label: 'Payment type', mobile: 'hide', width: '1fr' },
  { key: 'type', label: 'Type', mobile: 'hide', width: '0.7fr' },
  { key: 'amount', label: 'Amount received', sortable: true, mobile: 'label', width: '1.2fr' },
  { key: 'status', label: 'Status', sortable: true, mobile: 'label', width: '1fr' },
  { key: 'actions', label: 'Actions', mobile: 'hide', width: '80px', cellClass: 'text-center' },
  { key: 'details', label: 'Details', mobile: 'hide', width: '70px', cellClass: 'text-center' }
]

export default defineComponent({
  name: 'ReportsOutgoing',
  components: {
    TabLayout,
    PageHeading,
    AppButton,
    AppCheckbox,
    FormAlert,
    RowAlert,
    GridTable,
    Pagination,
    AppMultiselect,
    ReportsFilterBar,
    ReportsTotals,
    BulkEditorTool
  },
  setup() {
    const { proxy } = getCurrentInstance()
    const outgoing = useReportsOutgoing()
    const searchInput = ref('')
    const editingId = ref(null)
    const editAmount = ref('')
    const alertMessage = ref(null)
    const alertType = ref('success')
    let alertTimer = null

    function showAlert(message, type) {
      alertMessage.value = message
      alertType.value = type != null ? type : 'warning'
      clearTimeout(alertTimer)
      alertTimer = setTimeout(() => { alertMessage.value = null }, 5000)
    }

    // Results of actions on ONE row (inline payment type / status, amount, invoice PDF) appear right
    // above that row; bulk actions keep using the alert above the table. A row can leave the list when
    // a filter no longer matches it, so its message then goes above the row that took its place.
    const rowAlert = useRowAlert()

    function neighbourRowId(rowId) {
      const list = outgoing.items.value
      const index = list.findIndex(r => r.id === rowId)
      if (index === -1) return null
      const neighbour = list[index + 1] || list[index - 1]
      return neighbour != null ? neighbour.id : null
    }

    function notifyRow(rowId, neighbourId, message, type) {
      const anchor = outgoing.items.value.some(r => r.id === rowId) ? rowId : neighbourId
      if (anchor == null) {
        showAlert(message, type)
        return
      }
      alertMessage.value = null
      rowAlert.show(message, type, anchor)
    }

    function handleError(err, row = null) {
      const resp = err.response && err.response.data ? err.response.data : {}
      const type = resp.code === 'validation_error' ? 'danger' : 'warning'
      let message = resp.message != null ? resp.message : 'An error occurred.'
      if (resp.code === 'validation_error' && resp.errors != null) {
        const firstKey = Object.keys(resp.errors)[0]
        if (firstKey != null && Array.isArray(resp.errors[firstKey]) && resp.errors[firstKey][0] != null) {
          message = resp.errors[firstKey][0]
        }
      }
      if (row != null) {
        alertMessage.value = null
        rowAlert.show(message, type, row.id)
      } else {
        showAlert(message, type)
      }
    }

    const bulkPaymentType = ref(null)
    const bulkPaymentStatus = ref(null)
    const bulkRefund = ref(null)
    const bulkLoading = ref(false)
    const apcaNumber = ref('')
    const activeStatusFilter = ref('all')

    const { state: filterState, setYear: setFilterYear, setDraft: setFilterDraft, initFromQuery } = useReportsFilter(proxy.$router, () => {
      outgoing.filters.year = filterState.year
      outgoing.filters.draft_id = filterState.draftId
      outgoing.currentPage.value = 1
      outgoing.fetch()
      outgoing.fetchTotals()
      outgoing.fetchGenerationStatus(filterState.draftId)
    })

    const selectedCount = computed(() => outgoing.items.value.filter(r => r.selected).length)

    function onYearChange(val) {
      setFilterYear(val)
    }

    function onDraftChange(val) {
      setFilterDraft(val)
    }

    function onStatusFilter(val) {
      activeStatusFilter.value = val
      outgoing.setStatusFilter(val)
    }

    function onToggleAll() {
      const allSelected = selectedCount.value === outgoing.items.value.length
      outgoing.items.value.forEach((r) => { r.selected = !allSelected })
    }

    async function onApplyPaymentType() {
      if (bulkPaymentType.value == null || outgoing.selectedIds.value.length === 0) return
      bulkLoading.value = true
      try {
        const res = await reportsApi.updatePaymentType({ ids: outgoing.selectedIds.value, commit: bulkPaymentType.value.id })
        await outgoing.fetch()
        const msg = res.data && res.data.message ? res.data.message : 'Payment type updated.'
        showAlert(msg, 'success')
      } catch (err) {
        handleError(err)
      } finally {
        bulkLoading.value = false
      }
    }

    async function onApplyPaymentStatus() {
      if (bulkPaymentStatus.value == null || outgoing.selectedIds.value.length === 0) return
      bulkLoading.value = true
      try {
        const res = await reportsApi.updatePaymentStatus({
          reports: outgoing.selectedIds.value.map(id => ({ id })),
          status: bulkPaymentStatus.value.id
        })
        await outgoing.fetch()
        const msg = res.data && res.data.message ? res.data.message : 'Payment status updated.'
        showAlert(msg, 'success')
      } catch (err) {
        handleError(err)
      } finally {
        bulkLoading.value = false
      }
    }

    async function onApplyRefund() {
      if (bulkRefund.value == null || outgoing.selectedIds.value.length === 0) return
      bulkLoading.value = true
      try {
        const res = await reportsApi.updateRefunds({ report_ids: outgoing.selectedIds.value, status: bulkRefund.value.id })
        await outgoing.fetch()
        await outgoing.fetchTotals()
        const msg = res.data && res.data.message ? res.data.message : 'Refunds updated.'
        showAlert(msg, 'success')
      } catch (err) {
        handleError(err)
      } finally {
        bulkLoading.value = false
      }
    }

    function onGenerate() {
      if (filterState.draftId != null) {
        outgoing.generateReports(filterState.draftId)
      }
    }

    async function generateInvoicePdf(row) {
      const status = await outgoing.generateInvoicePdf(row)
      if (status === 'pending') {
        notifyRow(row.id, null, 'The file is still generating, please try again in a few seconds.', 'warning')
      }
    }

    function startEdit(row) {
      editingId.value = row.id
      editAmount.value = row.amount != null ? row.amount : ''
    }

    function cancelEdit() {
      editingId.value = null
      editAmount.value = ''
    }

    async function saveAmount(row) {
      const neighbour = neighbourRowId(row.id)
      try {
        const res = await reportsApi.updateOutgoingAmount(row.invoiceId, { amount: editAmount.value })
        editingId.value = null
        editAmount.value = ''
        await outgoing.fetch()
        await outgoing.fetchTotals()
        const msg = res.data && res.data.message ? res.data.message : 'Amount updated.'
        notifyRow(row.id, neighbour, msg, 'success')
      } catch (err) {
        editingId.value = null
        editAmount.value = ''
        handleError(err, row)
      }
    }

    async function onInlinePaymentType(row, value) {
      const neighbour = neighbourRowId(row.id)
      try {
        const res = await reportsApi.updatePaymentType({ ids: [row.id], commit: value })
        await outgoing.fetch()
        const msg = res.data && res.data.message ? res.data.message : 'Payment type updated.'
        notifyRow(row.id, neighbour, msg, 'success')
      } catch (err) {
        handleError(err, row)
      }
    }

    async function onInlinePaymentStatus(row, value) {
      const neighbour = neighbourRowId(row.id)
      try {
        const res = await reportsApi.updatePaymentStatus({ reports: [{ id: row.id }], status: Number(value) })
        await outgoing.fetch()
        const msg = res.data && res.data.message ? res.data.message : 'Payment status updated.'
        notifyRow(row.id, neighbour, msg, 'success')
      } catch (err) {
        handleError(err, row)
      }
    }

    const abaLoading = ref(false)

    async function onExportAba() {
      if (abaLoading.value) return
      abaLoading.value = true
      try {
        const res = await reportsApi.generateOutgoingAba({
          ids: outgoing.selectedIds.value,
          apca_number: apcaNumber.value
        })
        const disposition = res.headers['content-disposition']
        let filename = 'reports-outgoing.aba'
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/)
          if (match) filename = match[1]
        }
        const url = URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (err) {
        handleError(err)
      } finally {
        abaLoading.value = false
      }
    }

    let pollTimer = null
    function stopPolling() {
      if (pollTimer) {
        clearInterval(pollTimer)
        pollTimer = null
      }
    }
    function startPolling() {
      stopPolling()
      pollTimer = setInterval(async () => {
        await outgoing.fetchGenerationStatus(filterState.draftId)
        if (!outgoing.queueNotEmpty.value) {
          stopPolling()
          await outgoing.fetch()
          await outgoing.fetchTotals()
          showAlert('Prize money reports generated.', 'success')
        }
      }, 5000)
    }

    watch(() => outgoing.queueNotEmpty.value, (val) => {
      if (val) startPolling()
      else stopPolling()
    })

    onMounted(() => {
      outgoing.fetchOptions()
      initFromQuery(proxy.$route.query)
    })

    onBeforeUnmount(() => {
      stopPolling()
    })

    return {
      filterState,
      items: outgoing.items,
      totals: outgoing.totals,
      paymentTypes: outgoing.paymentTypes,
      statusOptions: outgoing.statusOptions,
      loading: outgoing.loading,
      generating: outgoing.generating,
      unfinalizedEvent: outgoing.unfinalizedEvent,
      queueNotEmpty: outgoing.queueNotEmpty,
      filters: outgoing.filters,
      currentPage: outgoing.currentPage,
      lastPage: outgoing.lastPage,
      setSearch: outgoing.setSearch,
      resetSearch: outgoing.resetSearch,
      setSort: outgoing.setSort,
      setPage: outgoing.setPage,
      setLimit: outgoing.setLimit,
      toggleSelectAll: outgoing.toggleSelectAll,
      exportReport: outgoing.exportReport,
      exportingFormat: outgoing.exportingFormat,
      abaLoading,
      generateInvoicePdf,
      generatingPdfIds: outgoing.generatingPdfIds,
      searchInput,
      editingId,
      editAmount,
      alertMessage,
      alertType,
      rowAlert,
      startEdit,
      cancelEdit,
      saveAmount,
      bulkPaymentType,
      bulkPaymentStatus,
      bulkRefund,
      bulkLoading,
      selectedCount,
      apcaNumber,
      activeStatusFilter,
      statusFilters: STATUS_FILTERS,
      outgoingColumns: OUTGOING_COLUMNS,
      onYearChange,
      onDraftChange,
      onStatusFilter,
      onToggleAll,
      refundTypes: outgoing.refundTypes,
      entriesOptions: ENTRIES_OPTIONS,
      onApplyPaymentType,
      onApplyPaymentStatus,
      onApplyRefund,
      onGenerate,
      onInlinePaymentType,
      onInlinePaymentStatus,
      onExportAba
    }
  }
})
</script>

<style scoped>
.bulk-action-group__label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.bulk-action-group__select {
  min-width: 220px;
}

.outgoing-pdf-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: outgoing-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes outgoing-spin {
  to { transform: rotate(360deg); }
}
</style>



// WEBPACK FOOTER //
// src/views/Reports/ReportsOutgoing.vue